import { useEffect, useState } from "react";
import { MdNotificationsActive, MdNotificationsOff } from "react-icons/md";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import InfoBlock from "../InfoBlock/InfoBlock";
import { ICON_SIZES, VAPID_PUBLIC_KEY } from "src/helpers/constants";
import apiClient from "src/helpers/api-client";
import { urlBase64ToUint8Array, showNotification } from "src/helpers/utils";
import { EButtonKind } from "../Button/ButtonTypes";
import { useTranslation } from "react-i18next";

interface IPushSubscription {
  hideIfEnabled?: boolean;
}

const PushSubscription: React.FC<IPushSubscription> = ({
  hideIfEnabled = false,
}) => {
  const { t } = useTranslation();
  const [showSubscribeDialog, setShowSubscribeDialog] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [endpoint, setEndpoint] = useState("");
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isUnsubscribing, setIsUnsubscribing] = useState(false);

  useEffect(() => {
    const checkSubscription = async () => {
      if (!("serviceWorker" in navigator)) return;

      const registration = await navigator.serviceWorker.ready;
      const existing = await registration.pushManager.getSubscription();
      if (existing) {
        setIsSubscribed(true);
        setEndpoint(existing.endpoint);
      }
    };

    checkSubscription();
  }, []);

  const subscribe = async () => {
    try {
      setIsSubscribing(true);
      const registration = await navigator.serviceWorker.ready;

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      const res = await apiClient.post("/notifications/subscribe", {
        subscription,
      });

      if (!res.data?.success) throw new Error("Error en el servidor");

      setIsSubscribed(true);
      setEndpoint(subscription.endpoint);

      showNotification({
        kind: "success",
        message: "Notificaciones push activadas",
      });
      setShowSubscribeDialog(false);
    } catch (err) {
      console.error("Error al suscribirse:", err);
      showNotification({
        kind: "error",
        message: "Error al activar las notificaciones",
      });
    } finally {
      setIsSubscribing(false);
    }
  };

  const unsubscribe = async () => {
    try {
      setIsUnsubscribing(true);
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
        await apiClient.post("/notifications/unsubscribe", {
          endpoint: subscription.endpoint,
        });

        showNotification({
          kind: "success",
          message: "Notificaciones push desactivadas",
        });

        setIsSubscribed(false);
        setEndpoint("");
        setShowDisableDialog(false);
      }
    } catch (err) {
      console.error("Error al desuscribirse:", err);
      showNotification({
        kind: "error",
        message: "Error al desactivar las notificaciones",
      });
    } finally {
      setIsUnsubscribing(false);
    }
  };

  if (hideIfEnabled && isSubscribed) return null;

  return (
    <>
      <div className="flex w-full flex-col gap-4 md:gap-6">
        <InfoBlock
          displayMobileIcon
          title={
            isSubscribed
              ? t("push_notifications.enabled_message_title")
              : t("push_notifications.disabled_message_title")
          }
          message={
            isSubscribed
              ? t("push_notifications.enabled_message")
              : t("push_notifications.disabled_message")
          }
          buttons={[
            isSubscribed
              ? {
                  tooltip: t("push_notifications.disable"),
                  icon: <MdNotificationsOff size={ICON_SIZES.MD} />,
                  onClick: () => setShowDisableDialog(true),
                  kind: EButtonKind.DANGER,
                  disabled: isUnsubscribing,
                  isLoading: isUnsubscribing,
                }
              : {
                  tooltip: t("push_notifications.enable"),
                  icon: <MdNotificationsActive size={ICON_SIZES.MD} />,
                  onClick: () => setShowSubscribeDialog(true),
                  kind: EButtonKind.SAVE,
                  disabled: isSubscribing,
                  isLoading: isSubscribing,
                },
          ]}
        />
      </div>

      {showSubscribeDialog && (
        <ConfirmDialog
          description="¿Deseas activar las notificaciones push en este dispositivo?"
          onClose={() => setShowSubscribeDialog(false)}
          onConfirm={async () => {
            await subscribe();
          }}
          title="Notificaciones"
          isLoading={isSubscribing}
          confirmKind={EButtonKind.SAVE}
        />
      )}

      {showDisableDialog && (
        <ConfirmDialog
          description="¿Deseas desactivar las notificaciones push en este dispositivo?"
          onClose={() => setShowDisableDialog(false)}
          onConfirm={async () => {
            await unsubscribe();
          }}
          title="Notificaciones"
          doubleConfirmRequired
          isLoading={isUnsubscribing}
          confirmKind={EButtonKind.DANGER}
        />
      )}
    </>
  );
};

export default PushSubscription;
